import React from 'react';
import PropTypes from 'prop-types';
import { Modal, Card } from 'react-bootstrap-5';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUpload,
  faTimes,
  faPaperclip,
  faDiamondExclamation,
} from '@fortawesome/pro-solid-svg-icons';
import { faCircleExclamation } from '@fortawesome/pro-regular-svg-icons';

const INVALID_TYPE_LABEL = 'Incorrect file type';

import { Button } from 'components/ui/button';
import { MAX_FILES } from './use_multi_doc_upload';

export default function DocumentsUploadModal({
  show,
  docs,
  fileDropError,
  isDragActive,
  canAddMore,
  getRootProps,
  getInputProps,
  removeDoc,
  onStartProcessing,
  onHide,
}) {
  const hasInvalidDocs = docs.some(d => d.status === 'invalid');
  const hasProcessableDocs = docs.some(d => d.status !== 'upload_error' && d.status !== 'invalid');
  const hasInvalidType = docs.some(d => d.invalidReason === 'invalid_type');
  const hasLimitExceeded = docs.some(d => d.invalidReason === 'limit_exceeded');

  const renderDropzone = () => {
    const disabled = !canAddMore;

    return (
      <Card
        className={[
          'border mb-3',
          disabled ? 'border-muted' : 'border-info',
          isDragActive ? 'bg-buildout-blue-50' : '',
        ].join(' ')}
        style={{
          borderStyle: 'dashed',
          transition: 'background 0.2s ease',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1,
          paddingBlock: '16px',
        }}
        {...getRootProps()}
      >
        <Card.Body className="py-4 text-center">
          <input {...getInputProps({ multiple: true })} />
          <div className={`mb-2 ${disabled ? 'text-muted' : 'text-accent'}`}>
            <FontAwesomeIcon icon={faUpload} size="xl" />
          </div>
          <p className="mb-1 fw-semibold-">
            {
              isDragActive ?
                'Drop files here' :
                <>Drop files here or <span className={disabled ? 'text-muted' : 'text-accent'}>click to upload</span></>
            }
          </p>
          <div className="text-muted small">
            We support PDF, Word, Excel, and Powerpoint. Up to {MAX_FILES} files.
          </div>
        </Card.Body>
      </Card>
    );
  };

  const renderDocList = () => {
    if (docs.length === 0) return null;

    return (
      <div className="d-flex flex-column gap-2 mb-3">
        {docs.map(doc => (
          <div
            className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom"
            key={doc.id}
          >
            <div className="d-flex align-items-center gap-2 overflow-hidden">
              <FontAwesomeIcon className="text-muted" icon={faPaperclip} />
              <span className="text-truncate small">{doc.file.name}</span>
              {doc.invalidReason === 'invalid_type' && (
                <span
                  className={[
                    'd-inline-flex align-items-center gap-1',
                    'bg-light rounded border px-2 py-1 small flex-shrink-0',
                  ].join(' ')}
                  style={{ borderColor: '#6c757d', color: '#6c757d' }}
                >
                  <FontAwesomeIcon icon={faDiamondExclamation} style={{ color: '#6c757d' }} />
                  {INVALID_TYPE_LABEL}
                </span>
              )}
              {doc.error && (
                <span className="text-destructive small ms-1">— {doc.error}</span>
              )}
            </div>
            <button
              aria-label="Remove file"
              className="btn btn-link btn-sm text-muted p-0 ms-2 flex-shrink-0"
              type="button"
              onClick={() => removeDoc(doc.id)}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        ))}
      </div>
    );
  };

  const renderValidationBanner = () => {
    if (!hasInvalidDocs) return null;

    return (
      <div className="alert alert-danger d-flex gap-2 small mb-3">
        <FontAwesomeIcon className="mt-1" icon={faCircleExclamation} />
        <div>
          <div className="fw-semibold mb-1">Some files need attention:</div>
          <ul className="mb-0 ps-3">
            {hasInvalidType && (
              <li>Files must be the correct file type (PDF/Word/Excel/Powerpoint)</li>
            )}
            {hasLimitExceeded && (
              <li>Upload limit is {MAX_FILES} files</li>
            )}
          </ul>
        </div>
      </div>
    );
  };

  return (
    <Modal centered show={show} onHide={onHide}>
      <Modal.Header closeButton style={{ paddingBlockEnd: '0px' }}>
        <Modal.Title>
          Create a New Listing
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ paddingBlockStart: '0px' }}>
        <p className="text-muted small mb-3">
          Upload your files and we'll extract the listing details. Processing may take a few minutes.
        </p>
        {fileDropError && (
          <div className="alert alert-warning small py-2 mb-3">{fileDropError}</div>
        )}
        {renderDropzone()}
        {renderValidationBanner()}
        {renderDocList()}
      </Modal.Body>
      <Modal.Footer>
        <Button label="Cancel" variant="secondary" onClick={onHide} />
        <Button
          disabled={!hasProcessableDocs || hasInvalidDocs}
          label="Upload Files"
          variant="primary"
          onClick={onStartProcessing}
        />
      </Modal.Footer>
    </Modal>
  );
}

DocumentsUploadModal.propTypes = {
  canAddMore: PropTypes.bool.isRequired,
  docs: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    file: PropTypes.object.isRequired,
    status: PropTypes.oneOf(['idle', 'uploading', 'upload_error', 'invalid']).isRequired,
    error: PropTypes.string,
    invalidReason: PropTypes.oneOf(['invalid_type', 'limit_exceeded']),
  })).isRequired,
  fileDropError: PropTypes.string,
  getInputProps: PropTypes.func.isRequired,
  getRootProps: PropTypes.func.isRequired,
  isDragActive: PropTypes.bool.isRequired,
  removeDoc: PropTypes.func.isRequired,
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onStartProcessing: PropTypes.func.isRequired,
};
